import type { FC } from 'react';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import { SecuritySearchableFilter } from './SecuritySearchableFilter';
import { useWazuhClientFilterOptions } from './useWazuhClientFilterOptions';

const DEFAULT_PILL_LABEL = 'All Clients';
const DEFAULT_ALL_LABEL = 'All Clients';
const DEFAULT_SEARCH_PLACEHOLDER = 'Search clients...';
const DEFAULT_EMPTY_MESSAGE = 'No clients found';
const DEFAULT_SELECTED_FALLBACK = 'Selected client';

export type SecurityClientFilterProps = {
  /**
   * Controlled selected client id (`null` = all).
   * Omit both `value` and `onChange` to bind to global WazuhScope (SOC header).
   */
  value?: string | null;
  onChange?: (clientId: string | null) => void;
  pillLabel?: string;
  allLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectedFallbackLabel?: string;
  showFilterIcon?: boolean;
  fullWidth?: boolean;
};

/**
 * Reseller-only child-org filter. Wraps SecuritySearchableFilter; data via useWazuhClientFilterOptions.
 */
export const SecurityClientFilter: FC<SecurityClientFilterProps> = ({
  value: controlledValue,
  onChange: controlledOnChange,
  pillLabel = DEFAULT_PILL_LABEL,
  allLabel = DEFAULT_ALL_LABEL,
  searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  selectedFallbackLabel = DEFAULT_SELECTED_FALLBACK,
  showFilterIcon = true,
  fullWidth = true,
}) => {
  const { isReseller, selectedClientId, setSelectedClientId } = useWazuhScope();
  const isControlled = controlledOnChange !== undefined;
  const selectedId = isControlled ? (controlledValue ?? null) : selectedClientId;

  const { options, loading, onSearch } = useWazuhClientFilterOptions(isReseller);

  const handleChange = (clientId: string | null) => {
    if (isControlled) {
      controlledOnChange?.(clientId);
    } else {
      setSelectedClientId(clientId);
    }
  };

  return (
    <SecuritySearchableFilter
      value={selectedId}
      onChange={handleChange}
      options={options}
      onSearch={onSearch}
      loading={loading}
      pillLabel={pillLabel}
      allLabel={allLabel}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      selectedFallbackLabel={selectedFallbackLabel}
      showFilterIcon={showFilterIcon}
      fullWidth={fullWidth}
      visible={isReseller}
    />
  );
};
